import type { OrganizationSpeciesOption } from "@/api/species/OrganizationSpeciesApi";
import {
  INTAKE_UNKNOWN_SPECIES_CODE,
  INTAKE_UNKNOWN_SPECIES_ID,
  INTAKE_UNKNOWN_SPECIES_NAME,
} from "@/ui/intake/intakeCopy";

/**
 * Anzeigename der gemeinsamen Art. Freitext schlägt den Katalog nicht,
 * solange eine Id gesetzt ist. Der Fallback intake-unknown heißt auch dann
 * „Unbekannt“, wenn der Katalog schon eine andere unknown-Zeile führt und
 * ensureIntakeUnknownSpecies ihn deshalb nicht noch einmal anhängt.
 */
export function intakeSharedSpeciesLabel(
  speciesId: string | null,
  freeText: string,
  options: readonly OrganizationSpeciesOption[],
): string | null {
  if (speciesId) {
    const named =
      ensureIntakeUnknownSpecies([...options]).find((option) => option.id === speciesId)?.name.trim() ??
      "";
    if (named.length > 0) return named;
    if (speciesId === INTAKE_UNKNOWN_SPECIES_ID) return INTAKE_UNKNOWN_SPECIES_NAME;
  }
  const text = freeText.trim();
  return text.length > 0 ? text : null;
}

/** Katalog oder Freitext, nie beides. Leer bleibt leer. */
export function intakeSpeciesForSave(input: {
  organizationSpeciesId: string | null;
  speciesFreeText: string;
}): { organizationSpeciesId: string | null; speciesFreeText: string | null } {
  const free = input.speciesFreeText.trim();
  if (free.length > 0) {
    return { organizationSpeciesId: null, speciesFreeText: free };
  }
  const id = input.organizationSpeciesId?.trim() ?? "";
  if (id === INTAKE_UNKNOWN_SPECIES_ID) {
    return { organizationSpeciesId: null, speciesFreeText: INTAKE_UNKNOWN_SPECIES_NAME };
  }
  return {
    organizationSpeciesId: id.length > 0 ? id : null,
    speciesFreeText: null,
  };
}

/** Wahr, wenn weder Katalog-Art noch Freitext gesetzt ist. */
export function intakeSpeciesMissing(input: {
  organizationSpeciesId: string | null;
  speciesFreeText: string;
}): boolean {
  const saved = intakeSpeciesForSave(input);
  return saved.organizationSpeciesId == null && saved.speciesFreeText == null;
}

/**
 * Hängt „Unbekannt“ an, wenn der Katalog keinen code=unknown liefert.
 * Eine vorhandene Zeile bleibt an ihrem Platz. Die Sortierung ans Ende liegt nicht hier.
 */
export function ensureIntakeUnknownSpecies(
  options: OrganizationSpeciesOption[],
): OrganizationSpeciesOption[] {
  if (options.some((option) => option.code === INTAKE_UNKNOWN_SPECIES_CODE)) return options;
  return [
    ...options,
    {
      id: INTAKE_UNKNOWN_SPECIES_ID,
      name: INTAKE_UNKNOWN_SPECIES_NAME,
      code: INTAKE_UNKNOWN_SPECIES_CODE,
    },
  ];
}
